import { useQuery } from '@tanstack/react-query';
import type { Id } from 'api/models';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { ReminderAiCallSchema } from 'modules/AiCalls/api/use-load-ai-call';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { ReminderSendingMediaSchema } from 'types/reminder-sending-media';
import { IgnoredReasonSchema, ReminderStatusSchema } from 'types/reminder-status';
import { EmailDeliveryStatus } from 'types/store/activity';
import { z } from 'zod/v4';

export const ReminderSchema = z.object({
  id: z.number(),
  sentAt: z.iso.datetime().transform(dayjs),
  debtorFullName: z.string(),
  debtorId: z.string(),
  reference: z.string(),
  reminderType: z.string(),
  sentMedia: z.array(ReminderSendingMediaSchema),
  invoiceCount: z.number(),
  resentVia: z.number().nullable(),
  original: z.number().nullable(),
  status: ReminderStatusSchema.nullable(),
  ignoredReason: IgnoredReasonSchema.nullable(),
  automaticResent: z.boolean(),
  letters: z.array(
    z.object({
      url: z.string(),
      trackingLink: z.string().nullable(),
    }),
  ),
  emails: z.array(
    z.object({
      body: z.string(),
      email: z.string(),
      sentAt: z.string(),
      statuses: z.partialRecord(z.enum(EmailDeliveryStatus), z.array(z.string())),
      subject: z.string().nullable(),
      failed: z.boolean(),
    }),
  ),
  aiCall: ReminderAiCallSchema,
});

export type Reminder = z.infer<typeof ReminderSchema>;

export async function loadReminderQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;

  const { data } = await instance.get(`/payment_requests/${queryKey[1]}`);

  const parseResult = ReminderSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'reminder');
    throw parseResult.error;
  }

  return parseResult.data as Reminder;
}

export const useLoadReminder = (id: Id) => {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Reminder, ApiError>({
    queryKey: ['reminders', id] as const,
    queryFn: context => loadReminderQueryFn(axiosInstance, context),
  });

  return addResourceNameToQueryResult<Reminder, unknown, 'reminder'>('reminder', queryResult);
};
